import React, { useState, useRef, useEffect } from "react";
import "./App.css";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProgressProvider } from "./context/ProgressContext";
import { GamificationProvider } from "./context/GamificationContext";
import { AnalyticsProvider } from "./context/AnalyticsContext";
import { ChallengesProvider } from "./context/ChallengesContext";
import Header from "./components/Header";
import ProgressModal from "./components/shared/ProgressModal";
import AchievementUnlocked from "./components/gamification/AchievementUnlocked";
import LevelUpModal from "./components/gamification/LevelUpModal";
import ChallengeCompleteToast from "./components/challenges/ChallengeCompleteToast";
import Grade1 from "./components/grades/Grade1";
import Grade2 from "./components/grades/Grade2";
import Grade3 from "./components/grades/Grade3";
import Grade4 from "./components/grades/Grade4";
import Grade5 from "./components/grades/Grade5";
import Grade6 from "./components/grades/Grade6";
import Translator from "./components/Translator";
import Dictionary from "./components/Dictionary";
import MessageBoard from "./components/MessageBoard/MessageBoard";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";

function AppContent() {
  const [currentGrade, setCurrentGrade] = useState(1);
  const [authPage, setAuthPage] = useState("login");
  const [showTranslator, setShowTranslator] = useState(false);
  const [showDictionary, setShowDictionary] = useState(false);
  const [showMessageBoard, setShowMessageBoard] = useState(false);
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  const gradeDropdownRef = useRef(null);
  const { user, loading, logout } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (gradeDropdownRef.current && !gradeDropdownRef.current.contains(event.target)) {
        setShowGradeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleTranslatorClick = () => {
    setShowTranslator(true);
    setShowDictionary(false);
    setShowMessageBoard(false);
  };

  const handleDictionaryClick = () => {
    setShowDictionary(true);
    setShowTranslator(false);
    setShowMessageBoard(false);
  };

  const handleMessageBoardClick = () => {
    setShowMessageBoard(true);
    setShowTranslator(false);
    setShowDictionary(false);
  };

  const renderGradeComponent = () => {
    switch (currentGrade) {
      case 1:
        return <Grade1 />;
      case 2:
        return <Grade2 />;
      case 3:
        return <Grade3 />;
      case 4:
        return <Grade4 />;
      case 5:
        return <Grade5 />;
      case 6:
        return <Grade6 />;
      default:
        return <Grade1 />;
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    if (authPage === "login") {
      return <LoginPage onSwitchToSignup={() => setAuthPage("signup")} />;
    }
    return <SignupPage onSwitchToLogin={() => setAuthPage("login")} />;
  }

  const handleGradeButtonClick = () => {
    setShowGradeDropdown(!showGradeDropdown);
  };

  const handleGradeSelect = (grade) => {
    setCurrentGrade(grade);
    setShowTranslator(false);
    setShowDictionary(false);
    setShowMessageBoard(false);
    setShowGradeDropdown(false);
  };

  return (
    <div className="App">
      <Header
        user={user}
        onLogout={logout}
        currentGrade={currentGrade}
        showTranslator={showTranslator}
        showDictionary={showDictionary}
        showMessageBoard={showMessageBoard}
        showGradeDropdown={showGradeDropdown}
        onGradeButtonClick={handleGradeButtonClick}
        onGradeSelect={handleGradeSelect}
        onTranslatorClick={handleTranslatorClick}
        onDictionaryClick={handleDictionaryClick}
        onMessageBoardClick={handleMessageBoardClick}
        gradeDropdownRef={gradeDropdownRef}
      />
      <ProgressModal />
      <AchievementUnlocked />
      <LevelUpModal />
      <ChallengeCompleteToast />

      <div className="main-layout no-sidebar">
        <div className="content">
          {showMessageBoard ? (
            <MessageBoard />
          ) : showDictionary ? (
            <Dictionary />
          ) : showTranslator ? (
            <Translator />
          ) : (
            renderGradeComponent()
          )}
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ProgressProvider>
          <GamificationProvider>
            <AnalyticsProvider>
              <ChallengesProvider>
                <AppContent />
              </ChallengesProvider>
            </AnalyticsProvider>
          </GamificationProvider>
        </ProgressProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
